'use client';
import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';
import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/app/utils/cn';

function Header({ children, isDarkMode = false, bgColor }) {
    const headerRef = useRef(null);
    const headerHeightRef = useRef(0);
    const [menuTop, setMenuTop] = useState(0);
    const [hasPromoBanner, setHasPromoBanner] = useState(false);
    const [promoBannerHeight, setPromoBannerHeight] = useState(0);

    const darkMode = Boolean(isDarkMode);

    useEffect(() => {
        if (!headerRef.current) {
            return undefined;
        }

        const updateMenuTop = () => {
            requestAnimationFrame(() => {
                const promoBannerElement = document.querySelector('[data-component="PromoBanner"]');
                const bannerHeight = promoBannerElement ? promoBannerElement.offsetHeight - 8 : 0;
                setHasPromoBanner(!!promoBannerElement);
                setPromoBannerHeight(bannerHeight);
                setMenuTop(headerHeightRef.current + 10);
            });
        };

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }

            requestAnimationFrame(() => {
                headerHeightRef.current = Math.round(entry.contentRect.height);
                updateMenuTop();
            });
        });

        resizeObserver.observe(headerRef.current);

        // Also observe the promo banner for size changes
        const promoBannerObserver = new ResizeObserver(() => {
            requestAnimationFrame(updateMenuTop);
        });

        const mutationObserver = new MutationObserver(() => {
            requestAnimationFrame(() => {
                updateMenuTop();
                // Re-observe promo banner if it was added
                const promoBannerElement = document.querySelector('[data-component="PromoBanner"]');
                if (promoBannerElement) {
                    promoBannerObserver.observe(promoBannerElement);
                }
            });
        });

        mutationObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['style'],
            childList: true,
            subtree: true
        });

        // Initial observation of promo banner
        const promoBannerElement = document.querySelector('[data-component="PromoBanner"]');
        if (promoBannerElement) {
            promoBannerObserver.observe(promoBannerElement);
        }

        updateMenuTop();

        return () => {
            resizeObserver.disconnect();
            promoBannerObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <div
            className={cn(
                { 'background-dark': darkMode, dark: darkMode },
                'fixed left-0 right-0 z-50 transition-all duration-300'
            )}
            style={{ top: hasPromoBanner ? `${promoBannerHeight}px` : '0' }}>
            <header
                className={
                    'bg-white/75 text-blue-700 backdrop-blur-lg dark:bg-blue-700 dark:text-white'
                }
                style={bgColor ? { backgroundColor: bgColor } : undefined}
                ref={headerRef}>
                <div className="container flex items-center justify-between py-4 md:py-6">
                    <LinkNoPrefetch href="/" className="flex items-center">
                        <div className="relative h-4 w-[5.5rem] md:h-4 md:w-[5.5rem] lg:h-5 lg:w-28 xl:w-[7.5rem]">
                            <Image
                                src={darkMode ? '/assets/logo-dark.svg' : '/assets/logo.svg'}
                                alt="dot CMS"
                                fill
                                sizes="(max-width: 768px) 100px, 120px"
                                className="object-contain"
                                unoptimized
                                priority
                            />
                        </div>
                    </LinkNoPrefetch>
                    {React.Children.map(children, (child) => {
                        return React.cloneElement(child, {
                            ...child.props,
                            position: menuTop,
                            promoBannerHeight: hasPromoBanner ? promoBannerHeight : 0
                        });
                    })}
                </div>
            </header>
        </div>
    );
}

export default Header;
